import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Globe } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What is a VAT invoice?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A VAT invoice is a document issued by a VAT-registered business that shows the VAT charged on goods or services. It is required in the UK, EU, and Australia for businesses that are VAT-registered. The invoice must show the supplier\'s VAT number, the VAT rate, the net amount, VAT amount, and total amount including VAT.',
      },
    },
    {
      '@type': 'Question',
      name: 'What information must a VAT invoice include?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'A valid VAT invoice must include: supplier name, address, and VAT registration number; invoice number (sequential) and invoice date; customer name and address; description of goods or services; net (pre-VAT) amount; VAT rate applied; VAT amount; and total amount including VAT.',
      },
    },
    {
      '@type': 'Question',
      name: 'Can I create a VAT invoice online free?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Invilic lets you create a VAT invoice online free. Enter your VAT number, select your VAT rate, add line items and the VAT amount and total are calculated automatically. Download as PDF instantly.',
      },
    },
    {
      '@type': 'Question',
      name: 'What VAT rates does Invilic support?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Invilic supports any VAT rate you enter. Common rates include UK VAT at 20%, EU standard VAT (varying by country from 17%–27%), Australian GST at 10%, and reduced/zero rates where applicable.',
      },
    },
    {
      '@type': 'Question',
      name: 'Do I need to be VAT-registered to issue a VAT invoice?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Yes. Only VAT-registered businesses are legally permitted to charge VAT and issue VAT invoices. If you are not VAT-registered, you should issue a standard invoice without VAT. Check your country\'s VAT registration threshold.',
      },
    },
  ],
};

export default function VatInvoiceGeneratorPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="VAT Invoice Generator: Free Online VAT Invoice Maker | Invilic"
        description="Create VAT-compliant invoices online free. Supports UK VAT, EU VAT, Australian GST. Auto-calculates VAT amount. Add VAT number, download PDF instantly."
        canonical="https://www.invilic.com/vat-invoice-generator"
        schema={schema}
      />

      <SeoNav />

      <main className="max-w-4xl mx-auto px-6 py-16">
        {/* Hero */}
        <div className="text-center mb-16">
          <span className="inline-block bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 text-xs font-semibold px-3 py-1 rounded-full uppercase tracking-wider mb-4">
            UK · EU · Australia
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 dark:text-white leading-tight mb-5">
            VAT Invoice Generator
          </h1>
          <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed mb-8">
            Create VAT-compliant invoices for UK, EU, and Australian businesses. Auto-calculates VAT amount. Add your VAT number and download a professional PDF for Free, no account required.
          </p>
          <Link
            to="/create/vat"
            className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-orange-500/20 transition-all duration-200 text-lg"
          >
            Create VAT Invoice Free <ArrowRight className="w-5 h-5" />
          </Link>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-3">No credit card · No account required</p>
        </div>

        {/* What's included */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            What's on Your VAT Invoice
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              'Supplier name, address & VAT number',
              'Customer name and address',
              'Sequential invoice number and date',
              'Line items with description and quantity',
              'Net amount (excluding VAT)',
              'VAT rate and auto-calculated VAT amount',
              'Total amount including VAT',
              'Payment terms and due date',
            ].map(f => (
              <div key={f} className="flex items-center gap-3 p-4 rounded-xl border border-orange-100 dark:border-orange-800/30 bg-orange-50 dark:bg-orange-900/10">
                <CheckCircle className="w-5 h-5 text-orange-500 dark:text-orange-400 flex-shrink-0" />
                <span className="text-sm text-gray-700 dark:text-gray-300">{f}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Regions */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">VAT by Region</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            {[
              { region: 'United Kingdom', detail: 'Standard VAT rate: 20%. VAT registration threshold: £90,000 turnover.', icon: '🇬🇧' },
              { region: 'European Union', detail: 'Standard rates range from 17% to 27% depending on country. EU VAT number format varies by member state.', icon: '🇪🇺' },
              { region: 'Australia', detail: 'GST rate: 10%. Registration threshold: AUD $75,000 annual turnover.', icon: '🇦🇺' },
            ].map(({ region, detail, icon }) => (
              <div key={region} className="p-5 rounded-xl border border-gray-100 dark:border-gray-800">
                <div className="text-2xl mb-3">{icon}</div>
                <div className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                  <Globe className="w-4 h-4 text-orange-500" />{region}
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">{detail}</p>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
            VAT Invoice: Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {schema.mainEntity.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-6 pb-5 text-gray-500 dark:text-gray-400 text-sm leading-relaxed">
                  {acceptedAnswer.text}
                </p>
              </details>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="text-center py-12 border-t border-gray-100 dark:border-gray-800">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Create Your VAT Invoice Now for Free
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-8">VAT-compliant PDF in under 2 minutes.</p>
          <Link
            to="/create/vat"
            className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-orange-500/20 transition-all duration-200 text-lg"
          >
            Create VAT Invoice <ArrowRight className="w-5 h-5" />
          </Link>
        </section>
      </main>
    </div>
  );
}

